import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProgressiveAuth } from '@/hooks/auth';
import { InlineLoader } from '@/components/ui/progressive-loading';

/**
 * Guard component that ensures a company is selected before rendering company workspace routes
 * Redirects to company hiring center if user has company access but no company selected
 */
export const CompanySelectionGuard = ({ children }: { children: React.ReactNode }) => {
  const { 
    hasCompanyAccess, 
    selectedCompanyId, 
    companies, 
    isFullyLoaded 
  } = useProgressiveAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isFullyLoaded) return; // Wait for data to load
    
    // If user has no companies at all, redirect to talent workspace
    if (hasCompanyAccess && companies.length === 0) {
      console.warn('[CompanySelectionGuard] No companies available, redirecting to talent');
      navigate('/workspace/talent/jobs', { replace: true });
      return;
    }
    
    // If has companies but none selected, redirect to neutral ground while auto-select happens
    if (hasCompanyAccess && companies.length > 0 && !selectedCompanyId) {
      console.warn('[CompanySelectionGuard] No company selected, redirecting to talent while auto-select happens');
      navigate('/workspace/talent/jobs', { replace: true });
    }
  }, [hasCompanyAccess, selectedCompanyId, companies, isFullyLoaded, navigate]);

  // Show loading state while initializing
  if (!isFullyLoaded) {
    return <InlineLoader />;
  }

  // Redirect to talent if no companies available
  if (hasCompanyAccess && companies.length === 0) {
    return <InlineLoader />;
  }

  // Show loading if waiting for company selection to sync
  if (hasCompanyAccess && !selectedCompanyId && companies.length > 0) {
    return <InlineLoader />;
  }

  // Only render children when company is actually selected
  return <>{children}</>;
};
